import {
	faArrowDown,
	faArrowLeft,
	faArrowRight,
	faArrowUp,
	faBackward,
	faBan,
	faBell,
	faBookmark,
	faBuilding,
	faCalendar,
	faChartBar,
	faChartLine,
	faChartPie,
	faCheck,
	faCheckCircle,
	faChevronDown,
	faCircle,
	faClock,
	faCog,
	faCoins,
	faComment,
	faCopy,
	faCreditCard,
	faDollarSign,
	faDotCircle,
	faDownload,
	faEdit,
	faEnvelope,
	faExclamationCircle,
	faExclamationTriangle,
	faEye,
	faEyeSlash,
	faFile,
	faFlag,
	faFolder,
	faForward,
	faGlobe,
	faHeart,
	faHome,
	faInfoCircle,
	faKey,
	faLink,
	faLock,
	faMapMarkerAlt,
	faMoneyBill,
	faMoneyCheckDollar,
	faPaperclip,
	faPause,
	faPhone,
	faPlay,
	faQuestionCircle,
	faShield,
	faShoppingCart,
	faSortDown,
	faSortUp,
	faSquare,
	faStar,
	faStop,
	faThumbsDown,
	faThumbsUp,
	faTimesCircle,
	faTrash,
	faUnlock,
	faUpload,
	faUser,
	faUsers,
	faWrench,
	faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../../../components/base/Icon';
import { ThemeAwareColorTiles } from '../../../../components/forms/ThemeAwareColorTiles';
import { resolveThemeColor } from '../../../../constants/theme-aware-colors';
import { Z_INDEX } from '../../../../constants/zIndex';
import { useTheme, useThemeColors } from '../../../../providers/ThemeProvider';

// Common table icons organized by category
const TABLE_ICONS = {
	Status: [
		{ icon: faCheck, name: 'check', label: 'Check' },
		{ icon: faXmark, name: 'xmark', label: 'X Mark' },
		{
			icon: faExclamationTriangle,
			name: 'exclamation-triangle',
			label: 'Warning',
		},
		{ icon: faInfoCircle, name: 'info-circle', label: 'Info' },
		{ icon: faBan, name: 'ban', label: 'Banned' },
		{ icon: faCheckCircle, name: 'check-circle', label: 'Success' },
		{ icon: faTimesCircle, name: 'times-circle', label: 'Error' },
		{ icon: faQuestionCircle, name: 'question-circle', label: 'Question' },
		{ icon: faExclamationCircle, name: 'exclamation-circle', label: 'Alert' },
	],
	Ratings: [
		{ icon: faStar, name: 'star', label: 'Star' },
		{ icon: faHeart, name: 'heart', label: 'Heart' },
		{ icon: faThumbsUp, name: 'thumbs-up', label: 'Thumbs Up' },
		{ icon: faThumbsDown, name: 'thumbs-down', label: 'Thumbs Down' },
		{ icon: faFlag, name: 'flag', label: 'Flag' },
		{ icon: faBookmark, name: 'bookmark', label: 'Bookmark' },
	],
	Communication: [
		{ icon: faComment, name: 'comment', label: 'Comment' },
		{ icon: faEnvelope, name: 'envelope', label: 'Email' },
		{ icon: faPhone, name: 'phone', label: 'Phone' },
		{ icon: faCalendar, name: 'calendar', label: 'Calendar' },
		{ icon: faClock, name: 'clock', label: 'Clock' },
		{ icon: faBell, name: 'bell', label: 'Bell' },
	],
	Business: [
		{ icon: faUser, name: 'user', label: 'User' },
		{ icon: faUsers, name: 'users', label: 'Users' },
		{ icon: faBuilding, name: 'building', label: 'Building' },
		{ icon: faHome, name: 'home', label: 'Home' },
		{ icon: faMapMarkerAlt, name: 'map-marker-alt', label: 'Location' },
		{ icon: faGlobe, name: 'globe', label: 'Globe' },
		{ icon: faShoppingCart, name: 'shopping-cart', label: 'Cart' },
		{ icon: faDollarSign, name: 'dollar-sign', label: 'Dollar' },
		{ icon: faCreditCard, name: 'credit-card', label: 'Credit Card' },
		{ icon: faCoins, name: 'coins', label: 'Coins' },
		{ icon: faMoneyBill, name: 'money-bill', label: 'Money Bill' },
		{
			icon: faMoneyCheckDollar,
			name: 'money-check-dollar',
			label: 'Money Check',
		},
	],
	Files: [
		{ icon: faFile, name: 'file', label: 'File' },
		{ icon: faFolder, name: 'folder', label: 'Folder' },
		{ icon: faPaperclip, name: 'paperclip', label: 'Attachment' },
		{ icon: faLink, name: 'link', label: 'Link' },
		{ icon: faDownload, name: 'download', label: 'Download' },
		{ icon: faUpload, name: 'upload', label: 'Upload' },
	],
	Actions: [
		{ icon: faEdit, name: 'edit', label: 'Edit' },
		{ icon: faTrash, name: 'trash', label: 'Delete' },
		{ icon: faCopy, name: 'copy', label: 'Copy' },
		{ icon: faEye, name: 'eye', label: 'View' },
		{ icon: faEyeSlash, name: 'eye-slash', label: 'Hidden' },
		{ icon: faLock, name: 'lock', label: 'Locked' },
		{ icon: faUnlock, name: 'unlock', label: 'Unlocked' },
	],
	Arrows: [
		{ icon: faArrowUp, name: 'arrow-up', label: 'Up' },
		{ icon: faArrowDown, name: 'arrow-down', label: 'Down' },
		{ icon: faArrowLeft, name: 'arrow-left', label: 'Left' },
		{ icon: faArrowRight, name: 'arrow-right', label: 'Right' },
		{ icon: faSortUp, name: 'sort-up', label: 'Sort Up' },
		{ icon: faSortDown, name: 'sort-down', label: 'Sort Down' },
	],
	Charts: [
		{ icon: faChartLine, name: 'chart-line', label: 'Line Chart' },
		{ icon: faChartBar, name: 'chart-bar', label: 'Bar Chart' },
		{ icon: faChartPie, name: 'chart-pie', label: 'Pie Chart' },
	],
	System: [
		{ icon: faCog, name: 'cog', label: 'Settings' },
		{ icon: faWrench, name: 'wrench', label: 'Tools' },
		{ icon: faKey, name: 'key', label: 'Key' },
		{ icon: faShield, name: 'shield', label: 'Shield' },
	],
	Media: [
		{ icon: faPlay, name: 'play', label: 'Play' },
		{ icon: faPause, name: 'pause', label: 'Pause' },
		{ icon: faStop, name: 'stop', label: 'Stop' },
		{ icon: faForward, name: 'forward', label: 'Forward' },
		{ icon: faBackward, name: 'backward', label: 'Backward' },
	],
	Shapes: [
		{ icon: faCircle, name: 'circle', label: 'Circle' },
		{ icon: faSquare, name: 'square', label: 'Square' },
		{ icon: faDotCircle, name: 'dot-circle', label: 'Dot Circle' },
	],
};

interface IconDropdownProps {
	visible: boolean;
	onClose: () => void;
	onSelect: (iconName: string, color: string) => void;
	currentIcon?: { name?: string; color?: string };
	triggerRef?: React.RefObject<HTMLElement>;
	isModalOpen?: boolean;
}

export function IconDropdown({
	visible,
	onClose,
	onSelect,
	currentIcon,
	triggerRef,
	isModalOpen = false,
}: IconDropdownProps) {
	const [selectedCategory, setSelectedCategory] = useState(
		Object.keys(TABLE_ICONS)[0],
	);
	const [selectedColor, setSelectedColor] = useState(
		currentIcon?.color ?? 'gray',
	);
	const [showColorDropdown, setShowColorDropdown] = useState(false);
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 700,
		height: 500,
	});
	const dropdownRef = useRef<HTMLDivElement>(null);
	const prevIsModalOpen = useRef(isModalOpen);
	const isMounted = useRef(false);
	const colors = useThemeColors();
	const { isDark } = useTheme();

	// Resolve the selected color for display
	const resolvedSelectedColor = resolveThemeColor(selectedColor, isDark);

	// Calculate dropdown position
	useEffect(() => {
		if (visible && triggerRef?.current) {
			const measure = () => {
				const element = triggerRef.current;
				if (element && element.getBoundingClientRect) {
					const rect = element.getBoundingClientRect();
					const windowHeight = window.innerHeight;
					const windowWidth = window.innerWidth;
					const dropdownWidth = 700;
					const dropdownHeight = 500;

					// Position below the trigger by default
					let top = rect.bottom + 4;
					let left = rect.left;

					// If not enough space below, position above
					if (
						windowHeight - rect.bottom < dropdownHeight &&
						rect.top > dropdownHeight
					) {
						top = rect.top - dropdownHeight - 4;
					}

					// If dropdown would go off right edge, align it to the right
					if (left + dropdownWidth > windowWidth) {
						left = windowWidth - dropdownWidth - 20;
					}

					setDropdownPosition({
						top,
						left,
						width: dropdownWidth,
						height: dropdownHeight,
					});
				}
			};

			// Small delay to ensure trigger is properly rendered
			setTimeout(measure, 10);
		}
	}, [visible, triggerRef]);

	// Close dropdown when modal opens
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			prevIsModalOpen.current = isModalOpen;
			return;
		}

		// Only close if isModalOpen changed from false to true
		if (!prevIsModalOpen.current && isModalOpen && visible) {
			onClose();
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, visible, onClose]);

	const handleSelectIcon = (iconName: string) => {
		onSelect(iconName, selectedColor);
		onClose();
	};

	if (!visible) {
		return null;
	}

	// Web implementation with portal
	if (typeof document !== 'undefined') {
		return createPortal(
			<>
				{/* Invisible backdrop to catch outside clicks */}
				<div
					className="fixed inset-0 bg-transparent"
					style={{
						zIndex: isModalOpen
							? Z_INDEX.modalPopoverBackdrop
							: Z_INDEX.popoverBackdrop,
					}}
					onClick={(e) => {
						// Only close if clicking the backdrop itself
						if (e.target === e.currentTarget) {
							onClose();
						}
					}}
				/>

				{/* Icon dropdown */}
				<div
					ref={dropdownRef}
					className={`fixed ${colors.classes.bgPrimary} rounded-xl shadow-lg overflow-hidden flex`}
					style={{
						top: dropdownPosition.top,
						left: dropdownPosition.left,
						width: dropdownPosition.width,
						height: dropdownPosition.height,
						zIndex: isModalOpen ? Z_INDEX.modalPopover : Z_INDEX.popover,
					}}
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					{/* Left sidebar with categories */}
					<div
						className={`w-[150px] ${colors.classes.bgSecondary} border-r ${colors.classes.borderDefault}`}
					>
						{/* Sidebar Header */}
						<div className={`p-4 border-b ${colors.classes.borderDefault}`}>
							<h4
								className={`text-sm font-semibold ${colors.classes.textSecondary}`}
							>
								Categories
							</h4>
						</div>

						{/* Category List */}
						<div className="overflow-y-auto">
							{Object.keys(TABLE_ICONS).map((category) => (
								<button
									key={category}
									onClick={() => {
										setSelectedCategory(category);
									}}
									className={`
                    w-full px-4 py-3 text-left border-l-[3px] transition-colors
                    ${
											selectedCategory === category
												? `${colors.classes.bgInfoLight} ${colors.classes.borderInfo} ${colors.classes.textInfo} font-semibold`
												: `border-transparent ${colors.classes.bgPrimaryHover} ${colors.classes.textSecondary} font-medium`
										}
                  `}
								>
									<span className="text-sm">{category}</span>
								</button>
							))}
						</div>
					</div>

					{/* Right content area */}
					<div className="flex-1 flex flex-col">
						{/* Header */}
						<div
							className={`flex justify-between items-center p-4 border-b ${colors.classes.borderDefault}`}
						>
							<h3
								className={`text-lg font-semibold ${colors.classes.textPrimary}`}
							>
								Select Icon
							</h3>
							<div className="flex items-center gap-3">
								{/* Color Selector */}
								<div className="relative">
									<button
										onClick={() => {
											setShowColorDropdown(!showColorDropdown);
										}}
										className={`flex items-center gap-2 py-1.5 px-2.5 border ${colors.classes.borderDefault} rounded-md ${colors.classes.bgPrimary} ${colors.classes.bgPrimaryHover}`}
									>
										<div
											className={`w-5 h-5 rounded border ${colors.classes.borderDefault}`}
											style={{ backgroundColor: resolvedSelectedColor }}
										/>
										<Icon
											icon={faChevronDown}
											size="xs"
											color={colors.values.textMuted}
										/>
									</button>

									{showColorDropdown && (
										<div
											className={`absolute top-full right-0 mt-1 ${colors.classes.bgPrimary} border ${colors.classes.borderDefault} rounded-lg shadow-lg p-3`}
											style={{ minWidth: '220px' }}
										>
											<ThemeAwareColorTiles
												value={selectedColor}
												onChange={(colorId) => {
													setSelectedColor(colorId);
													setShowColorDropdown(false);
												}}
											/>
										</div>
									)}
								</div>

								<button
									onClick={onClose}
									className={`p-1 ${colors.classes.textSecondary} ${colors.classes.textPrimaryHover}`}
								>
									<Icon icon={faXmark} size="sm" />
								</button>
							</div>
						</div>

						{/* Icon Grid */}
						<div className="flex-1 overflow-y-auto p-4">
							<div className="grid grid-cols-[repeat(auto-fill,minmax(80px,1fr))] gap-2">
								{TABLE_ICONS[selectedCategory as keyof typeof TABLE_ICONS].map(
									(iconItem) => (
										<button
											key={iconItem.name}
											onClick={() => {
												handleSelectIcon(iconItem.name);
											}}
											className={`
                      flex flex-col items-center justify-center p-3 border rounded-lg
                      h-20 ${colors.classes.bgPrimary} ${colors.classes.bgPrimaryHover}
                      ${
												currentIcon?.name === iconItem.name
													? `${colors.classes.borderInfo} ${colors.classes.bgInfoLight}`
													: colors.classes.borderDefault
											}
                    `}
										>
											<Icon
												icon={iconItem.icon}
												size="lg"
												style={{ color: resolvedSelectedColor }}
											/>
											<span
												className={`text-xs ${colors.classes.textSecondary} mt-1.5 text-center`}
											>
												{iconItem.label}
											</span>
										</button>
									),
								)}
							</div>
						</div>
					</div>
				</div>
			</>,
			document.body,
		);
	}

	return null;
}

// Helper function to find icon by name (exported for use in other components)
export function findIconByName(iconName: string) {
	for (const category in TABLE_ICONS) {
		const found = TABLE_ICONS[category as keyof typeof TABLE_ICONS].find(
			(i) => i.name === iconName,
		);
		if (found) {
			return found.icon;
		}
	}
	return null;
}
